import React, { useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { useFetchWalletTransferBatchSummaryMutation } from "../../../features/tenant/WalletTransfer/walletTransferApi";
import dayjs from "dayjs";
import { HiDotsVertical } from "react-icons/hi";
import { Menu, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { getVendorName } from "../../../utils/getVendorName";

const WalletTransferLogs = () => {
    const navigate = useNavigate();
    const tenant_id = JSON.parse(sessionStorage.getItem("vendorData")).tenant_id;
    const token = sessionStorage.getItem("access_token");

    const [fetchSummary, { isLoading }] = useFetchWalletTransferBatchSummaryMutation();

    const [data, setData] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const fetchData = async (currentPage, rowsPerPage, start, end) => {
        try {
            const response = await fetchSummary({
                token,
                tenant_id,
                limit: rowsPerPage,
                offset: (currentPage - 1) * rowsPerPage,
                startDate: start,
                endDate: end
            }).unwrap();

            if (response.success) {
                setData(response.body.data);
                setTotalRows(Number(response.body.total));
            }
        } catch (error) {
            console.error("Failed to fetch transfer summary:", error);
        }
    };

    useEffect(() => {
        fetchData(page, perPage, startDate, endDate);
    }, [page, perPage]);

    const handlePageChange = (p) => {
        setPage(p);
    };

    const handlePerRowsChange = (newPerPage, p) => {
        setPerPage(newPerPage);
        setPage(p);
    };

    const onFilter = () => {
        setPage(1);
        fetchData(1, perPage, startDate, endDate);
    };

    const onReset = () => {
        setStartDate("");
        setEndDate("");
        setPage(1);
        fetchData(1, perPage, "", "");
    };

    const handleViewDetails = (row) => {
        navigate(`/genefied/${getVendorName()}/wallet-transfer-details/${row.batch_id}`);
    };

    const columns = [
        {
            name: "Batch ID",
            selector: (row) => row.batch_id,
            sortable: true,
            minWidth: "180px"
        },
        {
            name: "Source Wallet",
            selector: (row) => row.from_wallet === "main" ? "Main Wallet" : `${row.from_wallet} - ${row.scheme_name}`,
            sortable: true,
        },
        {
            name: "Destination Wallet",
            selector: (row) => row.to_wallet === "main" ? "Main Wallet" : `${row.to_wallet} - ${row.scheme_name}`,
            sortable: true,
        },
        {
            name: "Transfer Date",
            selector: (row) => dayjs(row.transfer_date).format("DD MMM YYYY, HH:mm"),
            sortable: true,
            minWidth: "160px"
        },
        {
            name: "Total Users",
            selector: (row) => row.total_users,
            sortable: true,
            center: true
        },
        {
            name: "Status (S/F/SK)",
            cell: (row) => (
                <div className="flex gap-1 text-[10px] font-bold">
                    <span className="text-green-600">S:{row.success_count}</span>
                    <span className="text-red-600">F:{row.failed_count}</span>
                    <span className="text-gray-500">SK:{row.skipped_count}</span>
                </div>
            ),
            minWidth: "120px"
        },
        {
            name: "Points Moved",
            selector: (row) => row.total_points_moved,
            sortable: true,
            right: true
        },
        {
            name: "Actions",
            cell: (row) => (
                <Menu as="div" className="relative inline-block text-left">
                    <div>
                        <Menu.Button className="flex items-center text-gray-400 hover:text-gray-600 focus:outline-none">
                            <HiDotsVertical className="h-5 w-5" aria-hidden="true" />
                        </Menu.Button>
                    </div>

                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                    >
                        <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <div className="py-1">
                                <Menu.Item>
                                    {({ active }) => (
                                        <button
                                            onClick={() => handleViewDetails(row)}
                                            className={`${active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                                                } block w-full px-4 py-2 text-left text-sm`}
                                        >
                                            View Details
                                        </button>
                                    )}
                                </Menu.Item>
                            </div>
                        </Menu.Items>
                    </Transition>
                </Menu>
            ),
            width: "80px",
            center: true
        }
    ];

    return (
        <div className="flex w-full bg-gray-200 h-full justify-center">
            <div className="w-full">
                <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold flex justify-between items-center pr-6">
                    Wallet Transfer Logs
                    <div className="text-sm">
                        <LinksComponent />
                    </div>
                </div>

                <div className="p-4">
                    <div className="bg-white rounded-xl shadow-md p-4 mb-4">
                        <div className="flex flex-wrap items-end gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={onFilter}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                >
                                    Search
                                </button>
                                <button
                                    onClick={onReset}
                                    className="border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-md overflow-hidden">
                        <DataTableComponent
                            columns={columns}
                            data={data}
                            loading={isLoading}
                            totalRows={totalRows}
                            handlePageChange={handlePageChange}
                            handlePerRowsChange={handlePerRowsChange}
                            paginationPerPage={perPage}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WalletTransferLogs;
